import { useState, useCallback, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { FolderPlus, Search, FolderOpen } from "lucide-react";
import { useDroppable, useDndContext } from "@dnd-kit/core";
import { useI18n } from "@/lib/i18n";
import { useAppData, emitDataChanged } from "@/hooks/useAppData";
import { useClipboard } from "@/hooks/useClipboard";
import { useGlobalSelection } from "@/hooks/useGlobalSelection";
import { addFolder, updateFolder, addHistoryItem, updateDataItem } from "@/lib/db";
import { emitPreviewRequest } from "@/events/previewBus";
import { deleteSelectedItems } from "@/lib/deleteActions";
import ItemContextMenu from "@/components/shared/ItemContextMenu";
import DeleteConfirmDialog from "@/components/shared/DeleteConfirmDialog";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { isHtml, isMarkdown, isJson, isUrl } from "@/lib/textUtils";
import FolderTreeNode from "./FolderTreeNode";
import type { FolderItem, HistoryItem } from "@/types";

import { folderBus } from "@/events/folderBus";

const FolderArea = () => {
  const { t } = useI18n();
  const { folders, folderLinks, getDataById, loading } = useAppData();
  const { copyToClipboard } = useClipboard();
  const globalSelection = useGlobalSelection();
  const [search, setSearch] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const { active } = useDndContext();
  const viewportRef = useRef<HTMLDivElement>(null);

  // 드래그 상태에 따른 실시간 스크롤 억제 및 종료 시 비동기 원복
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    
    if (!active) {
      // 드래그 종료 후 약간의 지연을 두어 확실하게 리셋 (브라우저 기본 동작 간섭 방지)
      timeoutId = setTimeout(() => {
        if (viewportRef.current) {
          viewportRef.current.scrollLeft = 0;
        }
      }, 0);
    }

    const handleScroll = () => {
      if (active && viewportRef.current && viewportRef.current.scrollLeft !== 0) {
        viewportRef.current.scrollLeft = 0;
      }
    };

    const viewport = viewportRef.current;
    if (viewport) {
      viewport.addEventListener('scroll', handleScroll);
    }

    return () => {
      clearTimeout(timeoutId);
      if (viewport) {
        viewport.removeEventListener('scroll', handleScroll);
      }
    };
  }, [active]);

  const { setNodeRef: setRootDropRef, isOver: isOverRoot } = useDroppable({
    id: "folder-root-drop",
    data: { area: "folder-root" },
  });

  const rootFolders = folders
    .filter((f) => f.parentId === null)
    .sort((a, b) => a.order - b.order);

  const rootLinks = folderLinks.filter(
    (l) => !folders.some((f) => f.id === l.folderId)
  );

  const filteredFolders = search.trim()
    ? rootFolders.filter((f) => f.name.toLowerCase().includes(search.toLowerCase()))
    : rootFolders;

  const handleNewFolder = useCallback(async (parentId: string | null = null) => {
    const siblings = folders.filter(f => f.parentId === parentId);
    const maxOrder = siblings.length > 0 ? Math.max(...siblings.map(f => f.order)) + 1 : 0;
    const newId = crypto.randomUUID();
    const newFolder: FolderItem = {
      id: newId,
      name: t("folder.new"),
      parentId,
      order: maxOrder,
      createdAt: Date.now(),
    };
    await addFolder(newFolder);
    
    if (parentId) {
      folderBus.emitFolderOpen(parentId);
    }
    
    globalSelection.clearAll();
    globalSelection.addEntries([{ area: 'folder-node', itemId: newId }]);
    
    emitDataChanged();
  }, [folders, t, globalSelection]);

  const handleRenameFolder = useCallback(async (id: string, newName: string) => {
    const folder = folders.find((f) => f.id === id);
    if (!folder) return;
    await updateFolder({ ...folder, name: newName });
    emitDataChanged();
  }, [folders]);

  const handleRenameDataItem = useCallback(async (id: string, newLabel: string) => {
    const data = getDataById(id);
    if (!data) return;
    await updateDataItem({ ...data, label: newLabel });
    emitDataChanged();
  }, [getDataById]);

  const handleMoveFolder = useCallback(async (folderId: string, direction: 'up' | 'down') => {
    const siblings = folders
      .filter(f => f.parentId === (folders.find(fo => fo.id === folderId)?.parentId ?? null))
      .sort((a, b) => a.order - b.order);
    
    const idx = siblings.findIndex(f => f.id === folderId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;

    const current = siblings[idx];
    const swap = siblings[swapIdx];
    
    await updateFolder({ ...current, order: swap.order });
    await updateFolder({ ...swap, order: current.order });
    emitDataChanged();
  }, [folders]);

  const handleItemClick = useCallback(async (linkId: string, dataId: string, e?: React.MouseEvent) => {
    const isMulti = e ? (e.metaKey || e.ctrlKey) : false;
    const data = getDataById(dataId);
    if (!data) return;

    if (isMulti) {
      const isSelected = globalSelection.entries.some(en => en.itemId === linkId);
      if (isSelected) {
        globalSelection.removeEntries([linkId]);
      } else {
        globalSelection.addEntries([{ area: 'folder', itemId: linkId, dataId }]);
      }
    } else {
      globalSelection.clearAll();
      globalSelection.addEntries([{ area: 'folder', itemId: linkId, dataId }]);
    }

    const isRich =
      data.mimeType === "text/html" ||
      (data.type === "text" && (isHtml(data.content) || isMarkdown(data.content) || isJson(data.content) || isUrl(data.content))) ||
      data.type === "url";
    
    const clipType = data.type === "image" ? "image" : 
                    (data.type === "url" || (data.type === "text" && isUrl(data.content))) ? "url" :
                    isRich ? (isHtml(data.content) ? "html" : isMarkdown(data.content) ? "markdown" : "json") : "text";

    emitPreviewRequest({
      id: data.id,
      type: clipType,
      content: data.content,
    });

    const ok = await copyToClipboard(data.content, (clipType === "html" || clipType === "image") ? clipType : "text");
    if (ok) toast.success(t("menu.copy") + " ✓");

    const historyEntry: HistoryItem = {
      id: crypto.randomUUID(),
      dataId: data.id,
      createdAt: Date.now(),
    };
    await addHistoryItem(historyEntry);
    emitDataChanged();
  }, [getDataById, copyToClipboard, t, globalSelection]);

  const handleRightClickSelect = useCallback((area: "folder" | "folder-node", itemId: string, dataId?: string) => {
    const isSelected = globalSelection.entries.some(en => en.itemId === itemId);
    if (!isSelected) {
      globalSelection.clearAll();
      globalSelection.addEntries([{ area, itemId, dataId }]);
    }
  }, [globalSelection]);

  const handleDeleteSelected = useCallback(async () => {
    const toDelete = globalSelection.entries.filter(e => e.area === 'folder' || e.area === 'folder-node');
    if (toDelete.length === 0) return;
    await deleteSelectedItems(toDelete);
    globalSelection.clearArea('folder');
    globalSelection.clearArea('folder-node');
    setShowDeleteDialog(false);
  }, [globalSelection]);

  const selectedFolderIds = new Set(
    globalSelection.entries.filter(e => e.area === 'folder' || e.area === 'folder-node').map(e => e.itemId)
  );

  const hasContent = filteredFolders.length > 0 || rootLinks.length > 0;

  return (
    <>
      <div
        ref={setRootDropRef}
        className="h-full min-h-0 rounded-2xl transition-all duration-200"
      >
        <motion.section
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`glass-panel-static rounded-2xl p-4 flex flex-col h-full min-h-0 overflow-hidden transition-colors ${
            isOverRoot ? "ring-2 ring-inset ring-primary/50 bg-primary/5" : ""
          }`}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("folder.search")}
                data-testid="folder-search-input"
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg bg-background/40 border border-border/50 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                const selectedFolderId = Array.from(selectedFolderIds)[0];
                const folder = folders.find(f => f.id === selectedFolderId);
                handleNewFolder(folder ? folder.id : null);
              }}
              data-testid="add-folder-button"
              className="glass-bar rounded-lg p-1.5 shrink-0"
              title={t("folder.new")}
            >
              <FolderPlus className="w-4 h-4 text-primary" />
            </motion.button>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : !hasContent ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground/60 px-2">
                <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-xs leading-relaxed whitespace-pre-line">{t("folder.empty")}</p>
              </div>
            </div>
          ) : (
            <ScrollArea 
              viewportRef={viewportRef}
              className="flex-1 min-h-0 h-0 w-full overflow-x-hidden" 
              type="auto"
            >
              <div className="space-y-0.5 pr-1 overflow-x-hidden w-full">
                {filteredFolders.map((folder, index) => (
                  <FolderTreeNode
                    key={folder.id}
                    folder={folder}
                    folders={folders}
                    folderLinks={folderLinks}
                    getDataById={getDataById}
                    selectedIds={selectedFolderIds}
                    onSelect={(id, e) => {
                      const isMulti = e ? (e.metaKey || e.ctrlKey) : false;
                      if (isMulti) {
                        const isSelected = globalSelection.entries.some(en => en.itemId === id);
                        if (isSelected) {
                          globalSelection.removeEntries([id]);
                        } else {
                          globalSelection.addEntries([{ area: 'folder-node', itemId: id }]);
                        }
                      } else {
                        globalSelection.clearAll();
                        globalSelection.addEntries([{ area: 'folder-node', itemId: id }]);
                      }
                    }}
                    onRenameFolder={handleRenameFolder}
                    onRenameDataItem={handleRenameDataItem}
                    onMoveFolder={handleMoveFolder}
                    onNewFolder={handleNewFolder}
                    isFirst={index === 0}
                    isLast={index === filteredFolders.length - 1}
                    onItemClick={handleItemClick}
                    onRightClickSelect={handleRightClickSelect}
                    onDeleteRequest={() => setShowDeleteDialog(true)}
                    globalEntries={globalSelection.entries}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </motion.section>
      </div>


      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteSelected}
        count={globalSelection.entries.filter(e => e.area === 'folder' || e.area === 'folder-node').length}
      />
    </>
  );
};

export default FolderArea;
